/**
 * CallMediaStream - Media stream management for PeersCaller
 * Handles camera, microphone, and screen sharing streams
 */

import type { MediaStreamConfig, ScreenShareConfig } from "../types";

export class CallMediaStream {
  private stream: MediaStream | null = null;
  private videoElement: HTMLVideoElement | null = null;
  private constraints: MediaStreamConfig;
  private isScreenShare = false;

  constructor(constraints: MediaStreamConfig = { video: true, audio: true }) {
    this.constraints = constraints;
  }

  /**
   * Initialize media stream with camera and microphone
   */
  async initialize(): Promise<MediaStream> {
    try {
      console.log(
        "[CallMediaStream] Initializing media stream with constraints:",
        this.constraints
      );

      this.stream = await navigator.mediaDevices.getUserMedia(this.constraints);

      console.log("[CallMediaStream] Media stream initialized successfully");
      return this.stream;
    } catch (error) {
      console.error(
        "[CallMediaStream] Failed to initialize media stream:",
        error
      );
      throw this.handleMediaError(error);
    }
  }

  /**
   * Start screen sharing
   */
  async startScreenShare(
    config: ScreenShareConfig = { video: true, audio: false }
  ): Promise<MediaStream> {
    try {
      console.log("[CallMediaStream] Starting screen share");

      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: config.video,
        audio: config.audio,
      });

      // Store the previous camera stream
      const cameraStream = this.stream;

      // Replace current stream with screen stream
      this.stream = screenStream;
      this.isScreenShare = true;

      // Handle screen share end event
      const videoTrack = screenStream.getVideoTracks()[0];
      videoTrack.addEventListener("ended", () => {
        console.log("[CallMediaStream] Screen share ended by user");
        this.stopScreenShare(cameraStream);
      });

      console.log("[CallMediaStream] Screen share started successfully");
      return screenStream;
    } catch (error) {
      console.error("[CallMediaStream] Failed to start screen share:", error);
      throw this.handleMediaError(error);
    }
  }

  /**
   * Stop screen sharing and revert to camera
   */
  async stopScreenShare(
    cameraStream?: MediaStream | null
  ): Promise<MediaStream> {
    try {
      console.log("[CallMediaStream] Stopping screen share");

      // Stop screen share tracks
      if (this.stream && this.isScreenShare) {
        this.stream.getTracks().forEach((track) => track.stop());
      }

      // Revert to camera stream or initialize a new one
      if (cameraStream) {
        this.stream = cameraStream;
      } else {
        this.stream = await this.initialize();
      }

      this.isScreenShare = false;

      console.log("[CallMediaStream] Reverted to camera stream");
      return this.stream;
    } catch (error) {
      console.error("[CallMediaStream] Failed to stop screen share:", error);
      throw this.handleMediaError(error);
    }
  }

  /**
   * Toggle audio track
   */
  toggleAudio(enabled: boolean): void {
    if (!this.stream) {
      console.warn("[CallMediaStream] No stream available for audio toggle");
      return;
    }

    const audioTracks = this.stream.getAudioTracks();
    audioTracks.forEach((track) => {
      track.enabled = enabled;
    });

    console.log(`[CallMediaStream] Audio ${enabled ? "enabled" : "disabled"}`);
  }

  /**
   * Toggle video track
   */
  toggleVideo(enabled: boolean): void {
    if (!this.stream) {
      console.warn("[CallMediaStream] No stream available for video toggle");
      return;
    }

    const videoTracks = this.stream.getVideoTracks();
    videoTracks.forEach((track) => {
      track.enabled = enabled;
    });

    console.log(`[CallMediaStream] Video ${enabled ? "enabled" : "disabled"}`);
  }

  /**
   * Create and return a video element for displaying the stream
   */
  createVideoElement(): HTMLVideoElement {
    const video = document.createElement("video");
    video.autoplay = true;
    video.playsInline = true;
    video.muted = true; // Mute local video to prevent feedback

    if (this.stream) {
      video.srcObject = this.stream;
    }

    this.videoElement = video;
    return video;
  }

  /**
   * Attach stream to an existing video element
   */
  attachToVideoElement(videoElement: HTMLVideoElement): void {
    if (this.stream) {
      videoElement.srcObject = this.stream;
      this.videoElement = videoElement;
    }
  }

  /**
   * Replace the current stream (useful for switching between camera and screen)
   */
  replaceStream(newStream: MediaStream): void {
    // Stop current stream tracks
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
    }

    this.stream = newStream;

    // Update video element if exists
    if (this.videoElement) {
      this.videoElement.srcObject = newStream;
    }

    console.log("[CallMediaStream] Stream replaced successfully");
  }

  /**
   * Get current stream
   */
  getStream(): MediaStream | null {
    return this.stream;
  }

  /**
   * Get video element
   */
  getVideoElement(): HTMLVideoElement | null {
    return this.videoElement;
  }

  /**
   * Check if current stream is screen share
   */
  getIsScreenShare(): boolean {
    return this.isScreenShare;
  }

  /**
   * Get audio track enabled state
   */
  isAudioEnabled(): boolean {
    if (!this.stream) return false;
    const audioTracks = this.stream.getAudioTracks();
    return audioTracks.length > 0 && audioTracks[0].enabled;
  }

  /**
   * Get video track enabled state
   */
  isVideoEnabled(): boolean {
    if (!this.stream) return false;
    const videoTracks = this.stream.getVideoTracks();
    return videoTracks.length > 0 && videoTracks[0].enabled;
  }

  /**
   * Get stream information
   */
  getStreamInfo() {
    if (!this.stream) return null;

    const videoTracks = this.stream.getVideoTracks();
    const audioTracks = this.stream.getAudioTracks();

    return {
      id: this.stream.id,
      videoTracks: videoTracks.length,
      audioTracks: audioTracks.length,
      isScreenShare: this.isScreenShare,
      videoEnabled: videoTracks.length > 0 ? videoTracks[0].enabled : false,
      audioEnabled: audioTracks.length > 0 ? audioTracks[0].enabled : false,
      videoConstraints:
        videoTracks.length > 0 ? videoTracks[0].getSettings() : null,
      audioConstraints:
        audioTracks.length > 0 ? audioTracks[0].getSettings() : null,
    };
  }

  /**
   * Stop all tracks and cleanup
   */
  cleanup(): void {
    console.log("[CallMediaStream] Cleaning up media stream");

    if (this.stream) {
      this.stream.getTracks().forEach((track) => {
        track.stop();
      });
      this.stream = null;
    }

    if (this.videoElement) {
      this.videoElement.srcObject = null;
      this.videoElement = null;
    }

    this.isScreenShare = false;
  }

  /**
   * Handle media errors and provide user-friendly messages
   */
  private handleMediaError(error: any): Error {
    let message = "Unknown media error";

    if (error.name === "NotAllowedError") {
      message = "Permission denied. Please allow camera and microphone access.";
    } else if (error.name === "NotFoundError") {
      message = "No camera or microphone found. Please check your devices.";
    } else if (error.name === "NotReadableError") {
      message =
        "Camera or microphone is already in use by another application.";
    } else if (error.name === "OverconstrainedError") {
      message =
        "Camera or microphone does not support the requested constraints.";
    } else if (error.name === "SecurityError") {
      message = "Media access blocked due to security restrictions.";
    } else if (error.name === "AbortError") {
      message = "Media access was aborted.";
    }

    return new Error(`${message} (${error.name})`);
  }

  /**
   * Get available media devices
   */
  static async getAvailableDevices(): Promise<{
    videoDevices: MediaDeviceInfo[];
    audioDevices: MediaDeviceInfo[];
  }> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();

      return {
        videoDevices: devices.filter((device) => device.kind === "videoinput"),
        audioDevices: devices.filter((device) => device.kind === "audioinput"),
      };
    } catch (error) {
      console.error(
        "[CallMediaStream] Failed to get available devices:",
        error
      );
      throw error;
    }
  }

  /**
   * Check if browser supports required features
   */
  static checkBrowserSupport(): {
    getUserMedia: boolean;
    getDisplayMedia: boolean;
    webRTC: boolean;
  } {
    return {
      getUserMedia: !!(
        navigator.mediaDevices && navigator.mediaDevices.getUserMedia
      ),
      getDisplayMedia: !!(
        navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia
      ),
      webRTC: !!window.RTCPeerConnection,
    };
  }
}
